import { useMemo } from 'react';

import {
  COURSE_ID,
  COACHING_PROGRAM_ID,
  COMMUNITY_ID,
  PODCAST_ID,
} from '../../../utils/constants';

import { PreviewLoader } from '../components/PreviewLoader';
import {
  Checkout,
  Coaching,
  Community,
  Course,
  EmailSequence,
  LandingPage,
  Podcast,
} from '../contents/mock_pages';

interface DYNAMIC_STEPS_OBJECT {
  [IDENTIFIER: string]: any;
}

const DYNAMIC_STEPS_DATA: DYNAMIC_STEPS_OBJECT = {
  [COURSE_ID]: {
    component: Course,
    content: [
      {
        title: 'Building your course',
        description: 'This is the course your customers will access.',
      },
      {
        title: 'Generating course outline',
        description:
          'A course contains multiple lessons. Add quizzes or live meetup sessions for engagement.',
      },
      {
        title: 'Writing your lessons',
        description:
          'Lessons can include videos, text, PDFs, presentations, and more.',
      },
    ],
  },
  [COACHING_PROGRAM_ID]: {
    component: Coaching,
    content: [
      {
        title: 'Building your coaching program',
        description: 'This is the coaching program your customers will access.',
      },
      {
        title: 'Generating your coaching sessions',
        description:
          'Coaching sessions are one-on-one or with a large group of students.',
      },
    ],
  },
  [COMMUNITY_ID]: {
    component: Community,
    content: [
      {
        title: 'Building your community',
        description: 'You can also make your community product free or paid.',
      },
      {
        title: 'Generating community topics',
        description:
          'Your community can also have live meetups, group challenges, leaderboards, and more.',
      },
    ],
  },
  [PODCAST_ID]: {
    component: Podcast,
    content: [
      {
        title: 'Building your podcast',
        description: 'Make your podcast product free or paid.',
      },
      {
        title: 'Generating podcast episodes',
        description:
          'Your customers can listen to podcasts on your website or through streaming platforms like Spotify or Apple podcasts.',
      },
    ],
  },
};

const DEFAULT_STEPS_DATA = [
  {
    component: LandingPage,
    content: [
      {
        title: 'Building your landing page',
        description:
          'Introduce them to your product and products. You don’t need an entire website to get started!',
      },
      {
        title: 'Generating copy & images',
        description: 'Highlight the benefits your customers will receive.',
      },
      {
        title: 'Creating your opt-in form',
        description: 'Collect email addresses from visitors.',
      },
    ],
  },
  {
    component: EmailSequence,
    content: [
      {
        title: 'Creating your email broadcast',
        description: 'Build a relationship and nurture your contacts.',
      },
      {
        title: 'Writing sales copy & images',
        description: 'Reinforce the value and benefits of your product.',
      },
    ],
  },
  {
    component: Checkout,
    content: [
      {
        title: 'Building your checkout page',
        description: 'Direct your contacts to purchase your product.',
      },
      {
        title: 'Adding payment options',
        description:
          'Provide different payment options to increase conversion.',
      },
      {
        title: 'Writing copy & adding images',
        description: 'Describe what’s included with purchase.',
      },
    ],
  },
];

interface PropTypes {
  userData: any;
  onStepChange: () => void;
}

export const Loading = ({
  userData: { plan_to_sell: planToSell },
  onStepChange,
}: PropTypes) => {
  const stepsData = useMemo(() => {
    return [...DEFAULT_STEPS_DATA, DYNAMIC_STEPS_DATA[planToSell]];
  }, [planToSell]);

  return <PreviewLoader onComplete={onStepChange} stepsData={stepsData} />;
};
